import { useState, useEffect } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { 
  format, 
  startOfDay, 
  endOfDay, 
  startOfWeek, 
  endOfWeek, 
  startOfMonth, 
  endOfMonth, 
  startOfYear, 
  endOfYear,
  addDays,
  addWeeks,
  addMonths,
  addYears,
  isFuture
} from 'date-fns';
import { supabase } from '@/lib/supabase';
import { useSearchParams } from 'react-router-dom';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

type TimeFrame = 'day' | 'week' | 'month' | 'year';

interface PaymentData {
  date: string;
  total_amount: number;
  cash_amount: number;
  card_amount: number;
  tip_amount: number;
  custom_amount: number;
}

interface TimeFrameOption {
  label: string;
  value: TimeFrame;
}

export default function AnalyticsPage() {
  const [searchParams] = useSearchParams();
  const [timeframe, setTimeframe] = useState<TimeFrame>('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [data, setData] = useState<PaymentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState<number>(0);

  const timeFrameOptions: TimeFrameOption[] = [
    { label: 'Day', value: 'day' },
    { label: 'Week', value: 'week' },
    { label: 'Month', value: 'month' },
    { label: 'Year', value: 'year' },
  ];

  // Handle URL parameter on mount
  useEffect(() => {
    const timeframeParam = searchParams.get('timeframe');
    if (timeframeParam && ['day', 'week', 'month', 'year'].includes(timeframeParam)) {
      setTimeframe(timeframeParam as TimeFrame);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchData();
  }, [timeframe, currentDate]);

  const getDateRange = () => {
    switch (timeframe) {
      case 'day':
        return {
          start: startOfDay(currentDate),
          end: endOfDay(currentDate),
        };
      case 'week':
        return {
          start: startOfWeek(currentDate, { weekStartsOn: 1 }),
          end: endOfWeek(currentDate, { weekStartsOn: 1 }),
        };
      case 'month':
        return {
          start: startOfMonth(currentDate),
          end: endOfMonth(currentDate),
        };
      case 'year':
        return {
          start: startOfYear(currentDate),
          end: endOfYear(currentDate),
        };
    }
  };

  const navigate = (direction: 'prev' | 'next') => {
    const modifier = direction === 'prev' ? -1 : 1;
    let newDate: Date;
    switch (timeframe) {
      case 'day':
        newDate = addDays(currentDate, modifier);
        break;
      case 'week':
        newDate = addWeeks(currentDate, modifier);
        break;
      case 'month':
        newDate = addMonths(currentDate, modifier);
        break;
      case 'year':
        newDate = addYears(currentDate, modifier);
        break;
      default:
        newDate = currentDate;
    }
    setCurrentDate(newDate);
  };

  const canNavigateNext = !isFuture(
    timeframe === 'day' ? addDays(currentDate, 1) :
    timeframe === 'week' ? addWeeks(currentDate, 1) :
    timeframe === 'month' ? addMonths(currentDate, 1) :
    addYears(currentDate, 1)
  );

  const fetchData = async () => {
    setLoading(true);
    try {
      const { start, end } = getDateRange();
      
      // Fetch payment analytics
      const analyticsQuery = supabase
        .from('payment_analytics')
        .select('*')
        .gte('date', start.toISOString())
        .lte('date', end.toISOString())
        .order('date', { ascending: true });

      // Fetch expenses for the same period
      const expensesQuery = supabase
        .from('expenses')
        .select('amount')
        .gte('date', start.toISOString())
        .lte('date', end.toISOString());

      const [analyticsResult, expensesResult] = await Promise.all([
        analyticsQuery,
        expensesQuery
      ]);
      
      if (analyticsResult.error) throw analyticsResult.error;
      if (expensesResult.error) throw expensesResult.error;
      
      setData(analyticsResult.data || []);
      
      // Calculate total expenses
      const totalExpenses = (expensesResult.data || [])
        .reduce((sum, expense) => sum + expense.amount, 0);
      setExpenses(totalExpenses);

    } catch (error) {
      console.error('Failed to fetch analytics data:', error);
      // Reset states on error
      setData([]);
      setExpenses(0);
    }
    setLoading(false);
  };

  const chartData = {
    labels: data.map(d => format(new Date(d.date), timeframe === 'day' ? 'HH:mm' : 'MMM dd')),
    datasets: [
      {
        label: 'Total Revenue',
        data: data.map(d => d.total_amount),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
        tension: 0.4,
      },
      {
        label: 'Card Payments',
        data: data.map(d => d.card_amount),
        borderColor: 'rgb(54, 162, 235)',
        backgroundColor: 'rgba(54, 162, 235, 0.5)',
        tension: 0.4,
      },
      {
        label: 'Cash Payments',
        data: data.map(d => d.cash_amount),
        borderColor: 'rgb(255, 206, 86)',
        backgroundColor: 'rgba(255, 206, 86, 0.5)',
        tension: 0.4,
      },
      {
        label: 'Tips',
        data: data.map(d => d.tip_amount),
        borderColor: 'rgb(153, 102, 255)',
        backgroundColor: 'rgba(153, 102, 255, 0.5)',
        tension: 0.4,
      },
      {
        label: 'Special',
        data: data.map(d => d.custom_amount),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
        tension: 0.4,
      }
    ]
  };

  const totalRevenue = data.reduce((sum, d) => sum + d.total_amount, 0);
  const totalTips = data.reduce((sum, d) => sum + d.tip_amount, 0);
  const totalCard = data.reduce((sum, d) => sum + d.card_amount, 0);
  const totalCash = data.reduce((sum, d) => sum + d.cash_amount, 0);
  const totalCustom = data.reduce((sum, d) => sum + d.custom_amount, 0);

  // ADDED: Net revenue calculation
  const netRevenue = totalRevenue - expenses;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(156, 163, 175, 0.1)',
        },
        ticks: {
          color: 'rgb(156, 163, 175)',
        }
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: 'rgb(156, 163, 175)',
        },
        display: true,
        afterFit: (scale: any) => {
          scale.paddingRight = 0;  // Remove right padding
        }
      },
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: 'rgb(156, 163, 175)',
        }
      }
    },
    layout: {
      padding: {
        left: 0,
        right: 0,
        top: 10,
        bottom: 0
      }
    },
    animation: {
      duration: 1000,
      easing: 'easeInOutQuart' as const,
    },
  };

  const barChartData = {
    labels: [''], // Remove the 'Payment Distribution' label entirely
    datasets: [
      {
        label: 'Card Payments',
        data: [totalCard],
        backgroundColor: 'rgba(54, 162, 235, 0.5)',
        borderColor: 'rgb(54, 162, 235)',
        borderWidth: 1,
      },
      {
        label: 'Cash Payments',
        data: [totalCash],
        backgroundColor: 'rgba(255, 206, 86, 0.5)',
        borderColor: 'rgb(255, 206, 86)',
        borderWidth: 1,
      },
      {
        label: 'Tips',
        data: [totalTips],
        backgroundColor: 'rgba(153, 102, 255, 0.5)',
        borderColor: 'rgb(153, 102, 255)',
        borderWidth: 1,
      },
      {
        label: 'Special',
        data: [totalCustom],
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
        borderColor: 'rgb(255, 99, 132)',
        borderWidth: 1,
      },
    ],
  };

  const barChartOptions = {
    ...chartOptions,
    indexAxis: 'y' as const,
    maintainAspectRatio: false,
    scales: {
      ...chartOptions.scales,
      y: {
        ...chartOptions.scales.y,
        ticks: {
          display: false, // Hide the y-axis labels completely
        },
        grid: {
          display: false, // Hide the y-axis grid lines
        },
      },
    },
  };

  return (
    <div className="space-y-6 px-4 pt-4">
      {/* Header with Time Frame Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-dark-text-primary">
          Revenue Analytics
        </h1>
        <div className="flex flex-wrap items-center gap-4 w-full sm:w-auto">
          {/* Replace buttons with dropdown */}
          <select
            value={timeframe}
            onChange={(e) => {
              setTimeframe(e.target.value as TimeFrame);
              setCurrentDate(new Date()); // Reset to current date when changing timeframe
            }}
            className="px-3 py-2 text-sm rounded-lg bg-white dark:bg-dark-bg-light border border-gray-200 dark:border-dark-border text-gray-900 dark:text-dark-text-primary focus:ring-2 focus:ring-primary-500"
          >
            {timeFrameOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          
          <div className="flex items-center gap-2 ml-auto sm:ml-0">
            <button
              onClick={() => navigate('prev')}
              className="p-2 rounded-lg text-gray-600 dark:text-dark-text-secondary hover:bg-gray-100 dark:hover:bg-dark-bg-lighter"
            >
              <i className="fas fa-chevron-left" />
            </button>
            <span className="text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
              {timeframe === 'day' && format(currentDate, 'MMM d, yyyy')}
              {timeframe === 'week' && `Week of ${format(getDateRange().start, 'MMM d')}`}
              {timeframe === 'month' && format(currentDate, 'MMMM yyyy')}
              {timeframe === 'year' && format(currentDate, 'yyyy')}
            </span>
            <button
              onClick={() => navigate('next')}
              disabled={!canNavigateNext}
              className={`p-2 rounded-lg ${
                canNavigateNext
                  ? 'text-gray-600 dark:text-dark-text-secondary hover:bg-gray-100 dark:hover:bg-dark-bg-lighter'
                  : 'text-gray-400 dark:text-dark-text-secondary/50 cursor-not-allowed'
              }`}
            >
              <i className="fas fa-chevron-right" />
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-6 gap-4">
        <div className="stat bg-white dark:bg-dark-bg-light p-4 rounded-lg shadow-sm border border-gray-200 dark:border-dark-border">
          <div className="stat-title text-gray-600 dark:text-dark-text-secondary text-sm">Total Revenue</div>
          <div className="stat-value text-primary-600 dark:text-primary-400 text-lg sm:text-xl">€{totalRevenue.toFixed(2)}</div>
        </div>
        <div className="stat bg-white dark:bg-dark-bg-light p-4 rounded-lg shadow-sm border border-gray-200 dark:border-dark-border">
          <div className="stat-title text-gray-600 dark:text-dark-text-secondary text-sm">Card Payments</div>
          <div className="stat-value text-blue-600 dark:text-blue-400 text-lg sm:text-xl">€{totalCard.toFixed(2)}</div>
        </div>
        <div className="stat bg-white dark:bg-dark-bg-light p-4 rounded-lg shadow-sm border border-gray-200 dark:border-dark-border">
          <div className="stat-title text-gray-600 dark:text-dark-text-secondary text-sm">Cash Payments</div>
          <div className="stat-value text-yellow-600 dark:text-yellow-400 text-lg sm:text-xl">€{totalCash.toFixed(2)}</div>
        </div>
        <div className="stat bg-white dark:bg-dark-bg-light p-4 rounded-lg shadow-sm border border-gray-200 dark:border-dark-border">
          <div className="stat-title text-gray-600 dark:text-dark-text-secondary text-sm">Total Tips</div>
          <div className="stat-value text-purple-600 dark:text-purple-400 text-lg sm:text-xl">€{totalTips.toFixed(2)}</div>
        </div>
        <div className="stat bg-white dark:bg-dark-bg-light p-4 rounded-lg shadow-sm border border-gray-200 dark:border-dark-border">
          <div className="stat-title text-gray-600 dark:text-dark-text-secondary text-sm">Special</div>
          <div className="stat-value text-pink-600 dark:text-pink-400 text-lg sm:text-xl">€{totalCustom.toFixed(2)}</div>
        </div>
        <div className="stat bg-white dark:bg-dark-bg-light p-4 rounded-lg shadow-sm border border-gray-200 dark:border-dark-border">
          <div className="stat-title text-gray-600 dark:text-dark-text-secondary text-sm">Net Revenue</div>
          <div className={`stat-value text-lg sm:text-xl ${netRevenue >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            €{netRevenue.toFixed(2)}
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="space-y-6">
        {/* Revenue Trends */}
        <div className="bg-white dark:bg-dark-bg-light p-6 rounded-lg shadow-sm border border-gray-200 dark:border-dark-border">
          <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-dark-text-primary">Revenue Trends</h3>
          <div className="h-[400px]">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600 dark:border-primary-400"></div>
              </div>
            ) : data.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <i className="fas fa-chart-line text-4xl text-gray-400 dark:text-dark-text-secondary mb-3"></i>
                  <p className="text-gray-500 dark:text-dark-text-secondary">No data available for this time frame</p>
                </div>
              </div>
            ) : (
              <Line data={chartData} options={chartOptions} />
            )}
          </div>
        </div>

        {/* Payment Methods Distribution */}
        <div className="bg-white dark:bg-dark-bg-light p-6 rounded-lg shadow-sm border border-gray-200 dark:border-dark-border">
          <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-dark-text-primary">Payment Methods</h3>
          <div className="h-[400px]">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600 dark:border-primary-400"></div>
              </div>
            ) : data.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <i className="fas fa-chart-bar text-4xl text-gray-400 dark:text-dark-text-secondary mb-3"></i>
                  <p className="text-gray-500 dark:text-dark-text-secondary">No data available for this time frame</p>
                </div>
              </div>
            ) : (
              <Bar
                data={barChartData}
                options={barChartOptions}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 